<template>
  <div class="divide-y divide-gray-100">
    <div class="px-4 py-3 md:px-6">
      <TextTitle>Jouw energierekening</TextTitle>
    </div>

    <template v-for="group in visibleGroups" :key="group.key">
      <div class="bg-gray-50 px-4 py-2 md:px-6">
        <p class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {{ group.label }}
        </p>
      </div>
      <template v-for="line in group.lines" :key="line.key">
        <InvoiceLineItem
          v-if="line.amount !== null"
          :line
          :is-definitive
          :granularity
          :loading-quarter
          :quarter-failed
          :default-row-limit
        />
      </template>
    </template>

    <div class="flex items-center justify-between bg-gray-50 px-4 py-3 md:px-6">
      <span class="text-sm font-medium text-gray-700">Totaal</span>
      <span class="font-semibold text-gray-900">
        {{ formatEuro(invoiceTotal) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue"
import { TextTitle } from "@repowerednl/ui-component-library"
import { formatEuro } from "@/helpers/energyBillFormat.js"
import InvoiceLineItem from "./InvoiceLineItem.vue"

const props = defineProps({
  /**
   * The invoice line groups to render, each as a labelled section. Expected
   * shape per group:
   * - key: unique identifier, used as the v-for key
   * - label: the section heading
   * - lines: the invoice lines in this group (see InvoiceLineItem's `line`
   *   prop); lines with `noData` or a null amount are filtered out, and empty
   *   groups are hidden
   */
  groups: { type: Array, required: true },
  /**
   * Whether the month's results are definitive. Passed through to each line so
   * provisional lines can be flagged.
   */
  isDefinitive: { type: Boolean, required: true },
  /**
   * The selected detail granularity (see GRANULARITY). Passed through to each
   * line.
   */
  granularity: { type: String, required: true },
  /**
   * Whether the per-quarter detail data is still being loaded (per line).
   */
  loadingQuarter: { type: Boolean, default: false },
  /**
   * Whether loading the per-quarter detail data failed (per line).
   */
  quarterFailed: { type: Boolean, default: false },
  /**
   * Number of detail rows shown per line before the "Meer rijen tonen" toggle
   * appears.
   */
  defaultRowLimit: { type: Number, default: 5 },
  /**
   * The summed total shown in the "Totaal" footer (null when unavailable).
   */
  invoiceTotal: { type: Number, default: null },
})

// Lines without data are not shown
const visibleGroups = computed(() =>
  props.groups
    .map((group) => ({
      ...group,
      lines: group.lines.filter((line) => !line.noData && line.amount !== null),
    }))
    .filter((group) => group.lines.length > 0),
)
</script>
